import React, { useEffect, useState } from "react";
import {
  getParentDashboardStats,
  ParentChildStats,
} from "../../../server/parent-dashboard/requests";
import {
  ArrowDown,
  ArrowUp,
  Activity,
  BookOpen,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
} from "lucide-react";
import ChildrenTimeBar from "./ChildrenTimeBar";

// Palette de couleurs raffinée
const COLORS = [
  "#4361EE",
  "#3A0CA3",
  "#F72585",
  "#4CC9F0",
  "#4895EF",
  "#560BAD",
  "#7209B7",
  "#B5179E",
  "#F15BB5",
  "#FEE440",
  "#00BBF9",
  "#00F5D4",
];

// Typage de StatCard
interface StatCardProps {
  title: string;
  value: number;
  color: string;
  icon: React.ElementType;
}
const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  color,
  icon: Icon,
}) => (
  <div
    className={`flex flex-col bg-white rounded-lg shadow-md p-6 transition-all duration-300 border-l-4 ${color} hover:shadow-lg`}
  >
    <div className="flex justify-between items-center mb-3">
      <div className="text-gray-500 text-sm font-medium">{title}</div>
      <Icon className="text-gray-400" size={18} />
    </div>
    <div className="text-3xl font-bold">
      {Number.isFinite(value) ? value : "Néant"}
    </div>
  </div>
);

// Typage de PieChart
interface PieChartProps {
  data: number[];
  labels: string[];
}
const PieChart: React.FC<PieChartProps> = ({ data, labels }) => {
  const total = data.reduce((a: number, b: number) => a + b, 0);
  let cumulative = 0;

  return (
    <div className="relative">
      <svg viewBox="0 0 100 100" className="w-56 h-56">
        {/* Cercle interne pour l'effet donut */}
        <circle cx="50" cy="50" r="30" fill="white" />

        {data.map((value: number, i: number) => {
          const percentage = value / total;
          const startAngle = (cumulative / total) * 2 * Math.PI;
          const endAngle = ((cumulative + value) / total) * 2 * Math.PI;

          const x1 = 50 + 40 * Math.sin(startAngle);
          const y1 = 50 - 40 * Math.cos(startAngle);
          const x2 = 50 + 40 * Math.sin(endAngle);
          const y2 = 50 - 40 * Math.cos(endAngle);

          const largeArc = percentage > 0.5 ? 1 : 0;
          const pathData = `M50,50 L${x1},${y1} A40,40 0 ${largeArc} 1 ${x2},${y2} Z`;

          cumulative += value;

          return (
            <path
              key={i}
              d={pathData}
              fill={COLORS[i % COLORS.length]}
              stroke="white"
              strokeWidth="1"
              className="transition-all duration-500 hover:opacity-90"
            />
          );
        })}

        {/* Cercle central avec total */}
        <text
          x="50"
          y="48"
          textAnchor="middle"
          className="text-2xl font-bold"
          fontSize="10"
        >
          {total}
        </text>
        <text
          x="50"
          y="58"
          textAnchor="middle"
          className="text-xs text-gray-400"
          fontSize="6"
        >
          Total
        </text>
      </svg>
    </div>
  );
};

// Typage de PieLegend
interface PieLegendProps {
  data: number[];
  labels: string[];
}
const PieLegend: React.FC<PieLegendProps> = ({ data, labels }) => {
  const total = data.reduce((a: number, b: number) => a + b, 0);

  return (
    <div className="grid grid-cols-2 gap-3 mt-4">
      {labels.map((label: string, i: number) => (
        <div
          key={label}
          className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 transition-all"
        >
          <span
            className="inline-block w-4 h-4 rounded-full"
            style={{ backgroundColor: COLORS[i % COLORS.length] }}
          ></span>
          <div className="flex flex-col">
            <span className="font-medium text-sm">{label}</span>
            <div className="flex items-center space-x-1">
              <span className="text-gray-700 font-semibold">
                {Number.isFinite(data[i]) ? data[i] : "Néant"}
              </span>
              <span className="text-gray-400 text-xs">
                {total > 0 && Number.isFinite(data[i])
                  ? `(${((data[i] / total) * 100).toFixed(0)}%)`
                  : "(Néant)"}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Diagramme en bande SVG amélioré
interface BarChartProps {
  status: Record<string, number>;
}
const BarChart: React.FC<BarChartProps> = ({ status }) => {
  const keys = ["in_standby", "in_processing", "resolved", "rejected"];
  const labels = ["En attente", "En cours", "Résolues", "Rejetées"];
  const values = keys.map((k) => status[k] || 0);
  const max = Math.max(...values, 1);
  const barColors = ["#FFC107", "#FF9800", "#4CAF50", "#F44336"];

  return (
    <div className="w-full flex flex-col items-center">
      <svg viewBox="0 0 200 110" className="w-full h-52">
        {/* Lignes de grille horizontales */}
        {[0, 1, 2, 3, 4].map((i) => (
          <line
            key={`grid-${i}`}
            x1="0"
            y1={80 - i * 20}
            x2="200"
            y2={80 - i * 20}
            stroke="#f0f0f0"
            strokeWidth="1"
          />
        ))}

        {values.map((v, i) => (
          <g key={i} className="transition-all duration-500">
            <rect
              x={15 + i * 45}
              y={80 - (v / max) * 70}
              width={30}
              height={(v / max) * 70}
              fill={barColors[i]}
              rx={4}
              className="transition-all duration-500 hover:opacity-90"
            />
            <text
              x={30 + i * 45}
              y={95}
              textAnchor="middle"
              fontSize="8"
              fill="#555"
              fontWeight="500"
            >
              {labels[i]}
            </text>
            <text
              x={30 + i * 45}
              y={80 - (v / max) * 70 - 8}
              textAnchor="middle"
              fontSize="10"
              fill="#333"
              fontWeight="bold"
            >
              {Number.isFinite(v) ? v : "Néant"}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

// Composant principal
const DashboardParent = () => {
  const [childrenStats, setChildrenStats] = useState<ParentChildStats[]>([]);
  const [selected, setSelected] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const response = await getParentDashboardStats();
        if (
          response.status === 200 &&
          response.body &&
          "childrenStats" in response.body &&
          Array.isArray(response.body.childrenStats)
        ) {
          setChildrenStats(response.body.childrenStats);
        } else {
          // Données de test basées sur le format fourni
          const testData: ParentChildStats[] = [
            {
              childName: "mc king",
              stats: {
                totalCount: 15,
                statusCount: {
                  in_standby: 3,
                  in_processing: 2,
                  resolved: 8,
                  rejected: 2,
                },
                subjectDistribution: {
                  Mathématiques: 6,
                  Français: 4,
                  Histoire: 3,
                  Sciences: 2,
                },
                daylyTimeSpent: {
                  lastWeek: {},
                  currentWeek: {
                    monday: { hours: 1, minutes: 30, seconds: 0 },
                    tuesday: { hours: 2, minutes: 15, seconds: 30 },
                    wednesday: { hours: 0, minutes: 45, seconds: 0 },
                    thursday: { hours: 1, minutes: 0, seconds: 0 },
                    friday: { hours: 0, minutes: 30, seconds: 0 },
                    saturday: { hours: 0, minutes: 0, seconds: 0 },
                    sunday: { hours: 0, minutes: 0, seconds: 0 },
                  },
                },
                weeklyTimeSpent: {
                  lastMonth: {},
                  currentMonth: {
                    week1: { hours: 5, minutes: 30, seconds: 0 },
                    week2: { hours: 4, minutes: 15, seconds: 30 },
                    week3: { hours: 6, minutes: 0, seconds: 0 },
                    week4: { hours: 3, minutes: 45, seconds: 0 },
                  },
                },
                monthlyTimeSpent: {
                  lastYear: {},
                  currentYear: {
                    January: { hours: 20, minutes: 30, seconds: 0 },
                    February: { hours: 18, minutes: 45, seconds: 0 },
                    March: { hours: 25, minutes: 15, seconds: 0 },
                    April: { hours: 0, minutes: 28, seconds: 484 },
                    May: { hours: 22, minutes: 0, seconds: 0 },
                    June: { hours: 15, minutes: 30, seconds: 0 },
                  },
                },
              },
            },
          ];
          setChildrenStats(testData);
        }
      } catch (err) {
        // En cas d'erreur, utiliser les données de test
        const testData: ParentChildStats[] = [
          {
            childName: "mc king",
            stats: {
              totalCount: 15,
              statusCount: {
                in_standby: 3,
                in_processing: 2,
                resolved: 8,
                rejected: 2,
              },
              subjectDistribution: {
                Mathématiques: 6,
                Français: 4,
                Histoire: 3,
                Sciences: 2,
              },
              daylyTimeSpent: {
                lastWeek: {},
                currentWeek: {
                  monday: { hours: 1, minutes: 30, seconds: 0 },
                  tuesday: { hours: 2, minutes: 15, seconds: 30 },
                  wednesday: { hours: 0, minutes: 45, seconds: 0 },
                  thursday: { hours: 1, minutes: 0, seconds: 0 },
                  friday: { hours: 0, minutes: 30, seconds: 0 },
                  saturday: { hours: 0, minutes: 0, seconds: 0 },
                  sunday: { hours: 0, minutes: 0, seconds: 0 },
                },
              },
              weeklyTimeSpent: {
                lastMonth: {},
                currentMonth: {
                  week1: { hours: 5, minutes: 30, seconds: 0 },
                  week2: { hours: 4, minutes: 15, seconds: 30 },
                  week3: { hours: 6, minutes: 0, seconds: 0 },
                  week4: { hours: 3, minutes: 45, seconds: 0 },
                },
              },
              monthlyTimeSpent: {
                lastYear: {},
                currentYear: {
                  January: { hours: 20, minutes: 30, seconds: 0 },
                  February: { hours: 18, minutes: 45, seconds: 0 },
                  March: { hours: 25, minutes: 15, seconds: 0 },
                  April: { hours: 0, minutes: 28, seconds: 484 },
                  May: { hours: 22, minutes: 0, seconds: 0 },
                  June: { hours: 15, minutes: 30, seconds: 0 },
                },
              },
            },
          },
        ];
        setChildrenStats(testData);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#131CC9]"></div>
      </div>
    );
  }

  if (error || childrenStats.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Aucune donnée
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {error || "Aucun enfant trouvé dans votre compte."}
          </p>
        </div>
      </div>
    );
  }

  const child = childrenStats[selected];
  const status = child.stats.statusCount;
  const subjectData = Object.values(child.stats.subjectDistribution);
  const subjectLabels = Object.keys(child.stats.subjectDistribution);

  return (
    <div className="space-y-6">
      {/* Header + Dropdown */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 bg-white p-6 rounded-lg shadow-md">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Tableau de bord parent
          </h1>
          <p className="text-gray-500 mt-1">
            Suivez la progression scolaire de vos enfants
          </p>
        </div>
        <div className="relative inline-block w-64">
          <select
            className="block w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 appearance-none cursor-pointer"
            value={selected}
            onChange={(e) => setSelected(Number(e.target.value))}
            style={{
              backgroundImage:
                'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>\')',
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 12px center",
              paddingRight: "42px",
            }}
          >
            {childrenStats.map((c, idx) => (
              <option key={c.childName} value={idx}>
                {c.childName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Résumé de l'enfant sélectionné */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-bold text-gray-800 mb-2">
          Résumé pour {child.childName}
        </h2>
        <p className="text-gray-600">
          Voici un aperçu des soumissions et des matières pour cet enfant.
        </p>
      </div>

      {/* Statistiques principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total des soumissions"
          value={child.stats.totalCount}
          color="border-blue-500"
          icon={Activity}
        />
        <StatCard
          title="En attente"
          value={status.in_standby}
          color="border-yellow-500"
          icon={Clock}
        />
        <StatCard
          title="En cours"
          value={status.in_processing}
          color="border-orange-500"
          icon={BookOpen}
        />
        <StatCard
          title="Résolues"
          value={status.resolved}
          color="border-green-500"
          icon={CheckCircle}
        />
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Diagramme en bande (bar chart) */}
        <div className="bg-white rounded-lg shadow-md p-6 flex flex-col transition-all duration-300 hover:shadow-lg">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-800">
              Soumissions par statut
            </h2>
            <Activity size={20} className="text-gray-400" />
          </div>
          <BarChart status={status} />
        </div>

        {/* Distribution par matière */}
        <div className="bg-white rounded-lg shadow-md p-6 flex flex-col transition-all duration-300 hover:shadow-lg">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-800">
              Distribution par matière
            </h2>
            <BookOpen size={20} className="text-gray-400" />
          </div>

          <div className="flex flex-col md:flex-row items-center justify-around">
            {subjectData.length > 0 ? (
              <>
                <PieChart data={subjectData} labels={subjectLabels} />
                <PieLegend data={subjectData} labels={subjectLabels} />
              </>
            ) : (
              <div className="flex flex-col items-center text-gray-400 py-12">
                <AlertCircle size={36} className="mb-4 text-gray-300" />
                <p>Aucune donnée de matière disponible.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Temps passé sur la plateforme - Nouveau composant pour l'enfant sélectionné */}
      <div className="grid grid-cols-1 gap-6">
        <ChildrenTimeBar
          selectedChildIndex={selected}
          childrenStats={childrenStats}
        />
      </div>

      {/* Statut des dernières soumissions */}
      <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-800">
            Tendances des soumissions
          </h2>
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-green-600 flex items-center">
              <ArrowUp size={16} className="mr-1" /> {status.resolved} résolues
            </span>
            <span className="text-sm font-medium text-red-600 flex items-center ml-4">
              <ArrowDown size={16} className="mr-1" /> {status.rejected}{" "}
              rejetées
            </span>
          </div>
        </div>

        <div className="relative overflow-x-auto rounded-lg">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3">
                  Statut
                </th>
                <th scope="col" className="px-6 py-3">
                  Nombre
                </th>
                <th scope="col" className="px-6 py-3">
                  Pourcentage
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white border-b">
                <td className="px-6 py-4 flex items-center">
                  <Clock size={16} className="mr-2 text-yellow-500" />
                  <span>En attente</span>
                </td>
                <td className="px-6 py-4 font-medium">{status.in_standby}</td>
                <td className="px-6 py-4">
                  {child.stats.totalCount > 0
                    ? `${(
                        (status.in_standby / child.stats.totalCount) *
                        100
                      ).toFixed(1)}%`
                    : "Néant"}
                </td>
              </tr>
              <tr className="bg-white border-b">
                <td className="px-6 py-4 flex items-center">
                  <BookOpen size={16} className="mr-2 text-orange-500" />
                  <span>En cours</span>
                </td>
                <td className="px-6 py-4 font-medium">
                  {status.in_processing}
                </td>
                <td className="px-6 py-4">
                  {child.stats.totalCount > 0
                    ? `${(
                        (status.in_processing / child.stats.totalCount) *
                        100
                      ).toFixed(1)}%`
                    : "Néant"}
                </td>
              </tr>
              <tr className="bg-white border-b">
                <td className="px-6 py-4 flex items-center">
                  <CheckCircle size={16} className="mr-2 text-green-500" />
                  <span>Résolues</span>
                </td>
                <td className="px-6 py-4 font-medium">{status.resolved}</td>
                <td className="px-6 py-4">
                  {child.stats.totalCount > 0
                    ? `${(
                        (status.resolved / child.stats.totalCount) *
                        100
                      ).toFixed(1)}%`
                    : "Néant"}
                </td>
              </tr>
              <tr className="bg-white">
                <td className="px-6 py-4 flex items-center">
                  <XCircle size={16} className="mr-2 text-red-500" />
                  <span>Rejetées</span>
                </td>
                <td className="px-6 py-4 font-medium">{status.rejected}</td>
                <td className="px-6 py-4">
                  {child.stats.totalCount > 0
                    ? `${(
                        (status.rejected / child.stats.totalCount) *
                        100
                      ).toFixed(1)}%`
                    : "Néant"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardParent;
